import { BrainSvg, UpwardsArrowSvg } from "@/assets/AiBotAssets";
import { motion } from "framer-motion";
import { Slider } from "@mui/material";

export function AIConfiguration({
  selectedModelSetting,
  setSelectedModelSetting,
  onDataChange,
  botData,
  selectedModel,
}) {
  const isAutoModel = selectedModel?.name === "auto";
  const aiConfigElement = aiConfigElements.find(
    (el) => normalize(el.modelName) === normalize(selectedModel?.name),
  ) || {
    modelName: selectedModel?.name,
    thinkingToggle: false,
    tempControl: false,
  };
  const canShowTemperature = !isAutoModel && !!aiConfigElement.tempControl;
  const canShowThinking = !!aiConfigElement.thinkingToggle;
  const defaultThinkingConfig = {
    enable: true,
    mode: "disabled",
    level: "minimal",
  };
  const canToggleThinking = OPTIONAL_THINKING_MODELS.some(
    (modelName) => normalize(modelName) === normalize(selectedModel?.name),
  );
  const shouldForceThinkingConfig = canShowThinking && !canToggleThinking;
  const resolvedThinkingConfig =
    selectedModelSetting?.thinkingConfig ??
    (shouldForceThinkingConfig ? defaultThinkingConfig : null);
  const normalizedAutoMode = normalizeMaestroMode(
    botData?.aiBotConfig?.maestroFlowConfig?.selectedMode,
  );
  const selectedAutoMode = normalizedAutoMode || "balanced";

  const handleAutoModeChange = (mode: MaestroModeOption) => {
    onDataChange?.("aiBotConfig", {
      ...(botData?.aiBotConfig ?? {}),
      maestroFlowConfig: {
        ...(botData?.aiBotConfig?.maestroFlowConfig ?? {}),
        selectedMode: mode,
      },
    });
  };

  const upsertModelSetting = (makeSetting) => {
    const existingAiConfig = botData?.aiConfig ?? { modelSettings: [] };
    const existingSettings = existingAiConfig.modelSettings ?? [];
    const current = existingSettings.find(
      (s) => s?.modelName === selectedModel?.name,
    );

    const updated = makeSetting(current);
    const nextThinkingConfig = resolveThinkingConfig(
      current,
      updated,
      shouldForceThinkingConfig,
      defaultThinkingConfig,
    );

    const finalSetting = {
      ...(current ?? defaultSettings),
      ...(updated ?? {}),
      thinkingConfig: nextThinkingConfig,
      modelName: selectedModel?.name,
    };

    setSelectedModelSetting(finalSetting);

    const otherSettings = existingSettings.filter(
      (s) => s?.modelName !== selectedModel?.name,
    );
    const newSettings = [...otherSettings, finalSetting];

    const newAiConfig = { ...existingAiConfig, modelSettings: newSettings };

    onDataChange?.("aiConfig", newAiConfig);
  };
  const defaultSettings = {
    modelName: selectedModel?.name,
    temperature: 0,
    thinkingConfig: canShowThinking ? defaultThinkingConfig : null,
    selected: true,
  };
  const clampTemperature = (value: number) => {
    if (isNaN(value)) return 0;
    const clamped = Math.max(0, Math.min(1, value));
    return Math.round(clamped * 10) / 10;
  };
  const effectiveLevel = resolvedThinkingConfig?.level || "minimal";
  const thinkingEnabled = shouldForceThinkingConfig
    ? true
    : (resolvedThinkingConfig?.enable ?? true);
  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2, ease: "easeInOut" }}
      className="overflow-hidden"
    >
      <motion.div
        className="flex flex-col gap-[20px]"
        initial={{ y: -20 }}
        animate={{ y: 0 }}
        exit={{ y: -20 }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
      >
        {/* {isAutoModel && (
          <div className="temp-control-wrapper flex w-full flex-col gap-[10px]">
            <h4 className="text-[14px] font-medium leading-normal text-[#444]">
              Auto Mode
            </h4>
            <div className="flex w-full items-center rounded-[10px] bg-[#f8f8f8] p-[10px]">
              <div className="flex w-full flex-wrap items-center gap-[8px] bg-[#fff] p-[16px]">
                {MAESTRO_MODE_OPTIONS.map((mode) => (
                  <motion.button
                    key={mode.value}
                    className="rounded-full border px-[14px] py-[8px] text-[13px] leading-[14px]"
                    onClick={() => handleAutoModeChange(mode.value)}
                    whileTap={{ scale: 0.98 }}
                    initial={false}
                    animate={{
                      backgroundColor:
                        selectedAutoMode === mode.value ? "#fb5f66" : "#f9f9f9",
                      color:
                        selectedAutoMode === mode.value ? "#ffffff" : "#666666",
                      borderColor:
                        selectedAutoMode === mode.value
                          ? "#fb5f66"
                          : "rgba(0,0,0,0.1)",
                      boxShadow:
                        selectedAutoMode === mode.value
                          ? "0 4px 12px rgba(251,95,102,0.25)"
                          : "0 0px 0px rgba(0,0,0,0)",
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 400,
                      damping: 30,
                    }}
                  >
                    {mode.label}
                  </motion.button>
                ))}
              </div>
            </div>
          </div>
        )} */}
        {canShowTemperature && (
          <div className="temp-control-wrapper flex w-full flex-col gap-[10px]">
            <h4 className="text-[14px] font-medium leading-normal text-[#444]">
              Set Response Temperature
            </h4>
            <div className="flex w-full items-center rounded-[10px] bg-[#f8f8f8] p-[10px]">
              <div className="flex w-full flex-col bg-[#fff] p-[16px]">
                <div className="flex items-center justify-between gap-[16px]">
                  <div className="flex w-full flex-col items-center">
                    <div className="flex w-full items-center justify-between rounded-[10px]">
                      <span className="text-[13px] leading-normal text-[#444]">
                        0
                      </span>
                      <span className="text-[13px] leading-normal text-[#444]">
                        1
                      </span>
                    </div>
                    <div className="temp-control-wrapper relative w-full">
                      <Slider
                        value={selectedModelSetting?.temperature ?? 0}
                        onChange={(e, newValue) => {
                          const temp = clampTemperature(newValue as number);
                          setSelectedModelSetting({
                            ...selectedModelSetting,
                            temperature: temp,
                          });
                          upsertModelSetting((setting) => ({
                            ...(setting ?? defaultSettings),
                            temperature: temp,
                          }));
                        }}
                        min={0}
                        max={1}
                        step={0.1}
                        aria-label="Temperature"
                        sx={{
                          color: "#fb5f66",
                          padding: "0px",
                          height: 8,
                          "& .MuiSlider-track": {
                            border: "none",
                            backgroundColor: "#fb5f66",
                          },
                          "& .MuiSlider-rail": {
                            opacity: 0.5,
                            backgroundColor: "#e5e5e5",
                          },
                          "& .MuiSlider-thumb": {
                            height: 20,
                            width: 20,
                            backgroundColor: "#fff",
                            border: "2px solid #fb5f66",
                            "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible":
                              {
                                boxShadow: "inherit",
                              },
                            "&::before": {
                              display: "none",
                            },
                          },
                          "& .MuiSlider-valueLabel": {
                            lineHeight: 1.2,
                            fontSize: 12,
                            background: "unset",
                            padding: 0,
                            width: 32,
                            height: 32,
                            borderRadius: "50% 50% 50% 0",
                            backgroundColor: "#fb5f66",
                            transformOrigin: "bottom left",
                            transform:
                              "translate(50%, -100%) rotate(-45deg) scale(0)",
                            "&::before": { display: "none" },
                            "&.MuiSlider-valueLabelOpen": {
                              transform:
                                "translate(50%, -100%) rotate(-45deg) scale(1)",
                            },
                            "& > *": {
                              transform: "rotate(45deg)",
                            },
                          },
                        }}
                      />
                    </div>

                    <div className="mt-[5px] flex w-full items-center justify-between">
                      <span className="text-[13px] leading-normal text-[#666]">
                        Low Creativity
                      </span>
                      <span className="text-[13px] leading-normal text-[#666]">
                        High Creativity
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-[5px]">
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="1"
                      value={selectedModelSetting?.temperature ?? 0}
                      onChange={(e) => {
                        const temp = clampTemperature(
                          parseFloat(e.target.value),
                        );
                        setSelectedModelSetting({
                          ...selectedModelSetting,
                          temperature: temp,
                        });
                        upsertModelSetting((setting) => ({
                          ...(setting ?? defaultSettings),
                          temperature: temp,
                        }));
                      }}
                      onBlur={(e) => {
                        const temp = clampTemperature(
                          parseFloat(e.target.value),
                        );
                        setSelectedModelSetting({
                          ...selectedModelSetting,
                          temperature: temp,
                        });
                        upsertModelSetting((setting) => ({
                          ...(setting ?? defaultSettings),
                          temperature: temp,
                        }));
                      }}
                      className="h-[40px] w-[70px] rounded-[4px] border border-[rgba(0,0,0,0.10)] bg-[#f7f7f7] !px-[24px] !py-[8px]"
                    />

                    <div className="flex flex-col rounded-[4px] border border-[rgba(0,0,0,0.10)]">
                      <button
                        onClick={() => {
                          const temp = clampTemperature(
                            (selectedModelSetting?.temperature ?? 0) + 0.1,
                          );
                          setSelectedModelSetting({
                            ...selectedModelSetting,
                            temperature: temp,
                          });
                          upsertModelSetting((setting) => ({
                            ...(setting ?? defaultSettings),
                            temperature: temp,
                          }));
                        }}
                        className="grid h-[20px] w-[20px] flex-shrink-0 place-items-center border-b border-b-[rgba(0,0,0,0.10)]"
                      >
                        <UpwardsArrowSvg />
                      </button>
                      <button
                        onClick={() => {
                          const temp = clampTemperature(
                            (selectedModelSetting?.temperature ?? 0) - 0.1,
                          );
                          setSelectedModelSetting({
                            ...selectedModelSetting,
                            temperature: temp,
                          });
                          upsertModelSetting((setting) => ({
                            ...(setting ?? defaultSettings),
                            temperature: temp,
                          }));
                        }}
                        className="grid h-[20px] w-[20px] flex-shrink-0 rotate-180 place-items-center"
                      >
                        <UpwardsArrowSvg />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-[16px] flex flex-wrap gap-[16px]">
                  {[
                    { label: "Precise (0.1)", value: 0.1 },
                    { label: "Focused (0.4)", value: 0.4 },
                    { label: "Balanced (0.7)", value: 0.7 },
                    { label: "Creative (0.9)", value: 0.9 },
                  ].map((preset) => (
                    <button
                      key={preset.label}
                      onClick={() => {
                        const temp = clampTemperature(preset.value);
                        setSelectedModelSetting({
                          ...selectedModelSetting,
                          temperature: temp,
                        });
                        upsertModelSetting((setting) => ({
                          ...(setting ?? defaultSettings),
                          temperature: temp,
                        }));
                      }}
                      className={`rounded-[8px] border px-[12px] py-[6px] text-[13px] transition-colors ${
                        Math.abs(
                          (selectedModelSetting?.temperature ?? 0) -
                            preset.value,
                        ) > 0.05
                          ? "border-[rgba(0,0,0,0.10)] bg-[#f9f9f9] text-[#666]"
                          : "text-[#444 border-[#fb5f66] bg-[#FFF8F8]"
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        {canShowThinking && (
          <div className="temp-control-wrapper flex w-full flex-col gap-[10px]">
            <h4 className="text-[14px] font-medium leading-normal text-[#444]">
              Thinking
            </h4>
            <div className="flex w-full items-center justify-between gap-[10px] rounded-[10px] bg-[#f8f8f8] p-[10px]">
              <div className="flex w-full flex-col items-center gap-[16px] bg-[#fff] p-[16px]">
                <div className="flex w-full items-center justify-between gap-[10px] border-b border-b-[#4444441A] pb-[16px]">
                  <div className="flex items-center gap-[10px]">
                    <div className="grid h-[50px] w-[50px] place-items-center rounded-[6px] bg-[#f9f9f9]">
                      <BrainSvg />
                    </div>
                    <div className="flex flex-col gap-[5px]">
                      <h4 className="text-[14px] font-medium leading-[15.4px] text-[#444]">
                        Configure Thinking
                      </h4>
                      <p className="text-[14px] leading-[15.4px] text-[#666]">
                        Configure thinking/reasoning capabilities for supported
                        large reasoning models.
                      </p>
                    </div>
                  </div>
                  {canToggleThinking && (
                    <div className="toggle-btn">
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={thinkingEnabled}
                          onChange={() => {
                            const nextEnabled = !thinkingEnabled;
                            setSelectedModelSetting({
                              ...selectedModelSetting,
                              thinkingConfig: {
                                ...selectedModelSetting?.thinkingConfig,
                                enable: nextEnabled,
                                mode: nextEnabled
                                  ? (selectedModelSetting?.thinkingConfig
                                      ?.mode ?? "disabled")
                                  : "disabled",
                              },
                            });
                            upsertModelSetting((setting) => ({
                              ...(setting ?? defaultSettings),
                              thinkingConfig: {
                                ...((setting ?? defaultSettings)
                                  .thinkingConfig ?? {}),
                                enable: nextEnabled,
                                mode: nextEnabled
                                  ? ((setting ?? defaultSettings).thinkingConfig
                                      ?.mode ?? "disabled")
                                  : "disabled",
                              },
                            }));
                          }}
                        />
                        <span className="toggle round"></span>
                      </label>
                    </div>
                  )}
                </div>
                {thinkingEnabled && (
                  <div className="grid w-full grid-cols-1 gap-[20px] lg:grid-cols-[repeat(auto-fit,minmax(400px,1fr))]">
                    <div className="flex w-full items-center justify-between gap-[10px] rounded-[10px] bg-[#f8f8f8] p-[16px]">
                      <p className="w-max whitespace-nowrap text-[14px] text-[#444]">
                        Thinking Level
                      </p>
                      <div className="flex items-center gap-[8px]">
                        {ThinkingLevels.map((level, idx) => (
                          <motion.button
                            key={`thinking-level-${level}-${idx}`}
                            className="rounded-[8px] border px-[12px] py-[8px] text-[13px] leading-[14.3px]"
                            onClick={() => {
                              setSelectedModelSetting({
                                ...selectedModelSetting,
                                thinkingConfig: {
                                  ...selectedModelSetting.thinkingConfig,
                                  enable: true,
                                  level: level.toLocaleLowerCase(),
                                },
                              });
                              upsertModelSetting((setting) => ({
                                ...(setting ?? defaultSettings),
                                thinkingConfig: {
                                  ...selectedModelSetting?.thinkingConfig,
                                  enable: true,
                                  level: level.toLocaleLowerCase(),
                                },
                              }));
                            }}
                            whileTap={{ scale: 0.98 }}
                            initial={false}
                            animate={{
                              backgroundColor:
                                effectiveLevel === level.toLocaleLowerCase()
                                  ? "#fb5f66"
                                  : "#f9f9f9",
                              color:
                                effectiveLevel === level.toLocaleLowerCase()
                                  ? "#ffffff"
                                  : "#666666",
                              borderColor:
                                effectiveLevel === level.toLocaleLowerCase()
                                  ? "#fb5f66"
                                  : "rgba(0,0,0,0.1)",
                              boxShadow:
                                effectiveLevel === level.toLocaleLowerCase()
                                  ? "0 4px 12px rgba(251,95,102,0.3)"
                                  : "0 0px 0px rgba(0,0,0,0)",
                            }}
                            transition={{
                              type: "spring",
                              stiffness: 400,
                              damping: 30,
                            }}
                          >
                            {level}
                          </motion.button>
                        ))}
                      </div>
                    </div>
                    <div className="flex w-full items-center justify-between gap-[10px] rounded-[10px] bg-[#f8f8f8] p-[16px]">
                      <p className="text-[14px] text-[#444]">
                        Show thinking snippets to the user
                      </p>
                      <div className="flex items-center">
                        <label className="switch">
                          <input
                            type="checkbox"
                            checked={
                              selectedModelSetting?.thinkingConfig?.mode ===
                              "partial"
                                ? true
                                : false
                            }
                            onChange={() => {
                              setSelectedModelSetting({
                                ...selectedModelSetting,
                                thinkingConfig: {
                                  ...selectedModelSetting.thinkingConfig,
                                  enable: true,
                                  mode:
                                    selectedModelSetting.thinkingConfig
                                      ?.mode === "partial"
                                      ? "disabled"
                                      : "partial",
                                },
                              });
                              const newMode =
                                selectedModelSetting?.thinkingConfig?.mode ===
                                "partial"
                                  ? "disabled"
                                  : "partial";
                              upsertModelSetting((setting) => ({
                                ...(setting ?? defaultSettings),
                                thinkingConfig: {
                                  ...selectedModelSetting?.thinkingConfig,
                                  enable: true,
                                  mode: newMode,
                                },
                              }));
                            }}
                          />
                          <span className="toggle round"></span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

const resolveThinkingConfig = (
  current: any,
  updated: any,
  shouldForceThinkingConfig: boolean,
  defaultThinkingConfig: { enable: boolean; mode: string; level: string },
) => {
  if (updated?.thinkingConfig) return updated.thinkingConfig;
  if (current?.thinkingConfig) return current.thinkingConfig;

  if (shouldForceThinkingConfig) return defaultThinkingConfig;

  return null;
};

export const ThinkingLevels = ["Minimal", "Low", "Medium", "High"];

const MAESTRO_MODE_OPTIONS = [
  { value: "lowCost", label: "Low Cost" },
  { value: "balanced", label: "Balanced" },
  { value: "quality", label: "Quality" },
] as const;

type MaestroModeOption = (typeof MAESTRO_MODE_OPTIONS)[number]["value"];

const normalizeMaestroMode = (mode?: string): MaestroModeOption | undefined => {
  if (mode === "lowCost" || mode === "balanced" || mode === "quality") {
    return mode;
  }
  if (mode === "speed" || mode === "low_cost") return "lowCost";
  return undefined;
};

const normalize = (name: string | undefined | null) => {
  if (!name) return "";
  return name
    .toLowerCase()
    .replace(
      /^(openai\/|anthropic\/|google\/|groq\/|deepseek\/|grok\/|openrouter\/)/,
      "",
    )
    .replace(/^(gpt-|claude-|gemini-)/, "")
    .replace(/ /g, "")
    .replace(/-/g, "");
};
// in future if we want to support thinking toggle for more models, we can just add them to this list without needing to update the aiConfigElements array
export const OPTIONAL_THINKING_MODELS = [
  "5-sonnet",
  "4.8-opus",
  "4.6-sonnet",
  "4.5-sonnet",
  "4.5-haiku",
  "4-sonnet",
  "3.7-sonnet",
  // "gpt-5.6-sol",
  "gpt-5.6-terra",
  "gpt-5.6-luna",
  "gpt-5.5",
  "gpt-5.4",
  "gpt-5.4-mini",
  "gpt-5.4-nano",
  "deepseek-v4-flash",
  "deepseek-v4-pro",
  "grok-4.3",
  "grok-3",
  "grok-3-fast",
  "grok-3-mini",
  "grok-3-mini-fast",
  "kimi-k2",
  "oss-120b",
  "moonshotai/kimi-k2.6",
  "moonshotai/kimi-k2.5",
  "moonshotai/kimi-k3",
  "glm-5.2",
  "glm-5.1",
  "glm-5-turbo",
  "glm-4.7",
  "glm-4.6",
  "glm-4.5",
  "2.5-flash",
  "Gemini 2.5 Flash",
  // Gemini 3.6 Flash & 3.5 Flash Lite omitted: reasoning is compulsory (not toggleable)
  // "Gemini 3 Flash Preview",
  // "Gemini 3.1 Flash Lite Preview",
  "Gemini 2.5 Flash Lite",
];

export const aiConfigElements = [
  {
    modelName: "4.1",
    thinkingToggle: false,
    tempControl: true,
  },
  {
    modelName: "4.1-mini",
    thinkingToggle: false,
    tempControl: true,
  },
  {
    modelName: "4.1-nano",
    thinkingToggle: false,
    tempControl: true,
  },
  {
    modelName: "4o",
    thinkingToggle: false,
    tempControl: true,
  },
  {
    modelName: "4o-mini",
    thinkingToggle: false,
    tempControl: true,
  },
  {
    modelName: "gpt-5",
    thinkingToggle: true,
    tempControl: false,
  },
  {
    modelName: "gpt-5.4",
    thinkingToggle: true,
    tempControl: false,
  },
  {
    modelName: "gpt-5.4-mini",
    thinkingToggle: true,
    tempControl: false,
  },
  {
    modelName: "gpt-5.4-nano",
    thinkingToggle: true,
    tempControl: false,
  },
  // {
  //   modelName: "gpt-5.6-sol",
  //   thinkingToggle: true,
  //   tempControl: false,
  // },
  {
    modelName: "gpt-5.6-terra",
    thinkingToggle: true,
    tempControl: false,
  },
  {
    modelName: "gpt-5.6-luna",
    thinkingToggle: true,
    tempControl: false,
  },
  {
    modelName: "gpt-5.5",
    thinkingToggle: true,
    tempControl: false,
  },
  {
    modelName: "gpt-5-mini",
    thinkingToggle: true,
    tempControl: false,
  },
  {
    modelName: "o1",
    thinkingToggle: true,
    tempControl: false,
  },
  {
    modelName: "o3-mini",
    thinkingToggle: true,
    tempControl: false,
  },
  {
    modelName: "3.5-sonnet",
    thinkingToggle: false,
    tempControl: true,
  },
  {
    modelName: "3.5-haiku",
    thinkingToggle: false,
    tempControl: true,
  },
  {
    modelName: "3.7-sonnet",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "5-sonnet",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "4-sonnet",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "4.5-sonnet",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "4.5-haiku",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "4.6-sonnet",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "4.8-opus",
    thinkingToggle: true,
    tempControl: false,
  },
  // {
  //   modelName: "4.7-opus",
  //   thinkingToggle: true,
  //   tempControl: false,
  // },
  {
    modelName: "deepseek-chat",
    thinkingToggle: false,
    tempControl: true,
  },
  {
    modelName: "deepseek-v4-flash",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "deepseek-v4-pro",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "claude-3.5-sonnet",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "claude-3-opus",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "grok-3",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "grok-3-fast",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "grok-3-mini",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "grok-3-mini-fast",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "oss-120b",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "2.5-pro",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "2.5-flash",
    thinkingToggle: true,
    tempControl: true,
  },
  // {
  //   modelName: "Gemini 3 Pro Preview",
  //   thinkingToggle: true,
  //   tempControl: true,
  // },
  // {
  //   modelName: "Gemini 3.1 Pro Preview",
  //   thinkingToggle: true,
  //   tempControl: true,
  // },
  // {
  //   modelName: "Gemini 3 Flash Preview",
  //   thinkingToggle: true,
  //   tempControl: true,
  // },
  // {
  //   modelName: "Gemini 3.1 Flash Lite Preview",
  //   thinkingToggle: true,
  //   tempControl: true,
  // },
  {
    modelName: "Gemini 3.6 Flash",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "Gemini 3.5 Flash Lite",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "Gemini 3.5 Flash",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "Gemini 2.5 Pro",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "Gemini 2.5 Flash",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "Gemini 2.5 Flash Lite",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "minimax/minimax-m2.5",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "minimax/minimax-m2.7",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "kimi-k2",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "moonshotai/kimi-k2.5",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "moonshotai/kimi-k2.6",
    thinkingToggle: true,
    tempControl: true,
  },
  {
    modelName: "moonshotai/kimi-k3",
    thinkingToggle: true,
    tempControl: true,
  },
];
